import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import HTML from "bundles/common/components/HTML";
import JobOfferShareButtons from "bundles/common/components/JobOfferShareButtons";
import SkillView from "bundles/Recruiter/components/SkillView";
import { annualyRemuneration, formatJobOfferLocation } from "bundles/common/utils/utils";

const SECTION_CLAMP = 380;

const bodyHtmlSx = {
  "& p": {
    margin: "0 0 0.65em 0",
    fontSize: 16,
    lineHeight: 1.55,
    color: "#1B1B1B",
    fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
  },
  "& ul": {
    margin: 0,
    paddingLeft: "1.35rem",
    fontSize: 16,
    lineHeight: 1.55,
    color: "#1B1B1B",
    fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
  },
  "& ol": {
    margin: 0,
    paddingLeft: "1.35rem",
    fontSize: 16,
    lineHeight: 1.55,
    color: "#1B1B1B",
    fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
  },
  "& li": { marginBottom: "0.35rem" },
};

const detailCardSx = (theme) => ({
  bgcolor: "#fff",
  borderRadius: "10px",
  border: `1px solid ${theme.palette.background.neutral}`,
  p: { xs: 2, md: 2.5 },
});

const sectionTitleSx = {
  fontSize: 16,
  fontWeight: 600,
  letterSpacing: 0.15,
  color: "#1B1B1B",
  fontFamily: "Segoe UI Semibold, Work Sans, sans-serif",
  mb: 1.5,
};

const ExpandableHtmlSection = ({ titleId, html, defaultExpanded = false, linkColor }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!html) return null;
  const plain = String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const long = plain.length > SECTION_CLAMP;

  return (
    <Box>
      <Typography component="h3" sx={sectionTitleSx}>
        <FormattedMessage id={titleId} />
      </Typography>
      <Box
        sx={{
          maxHeight: expanded ? "none" : 200,
          overflow: expanded ? "visible" : "hidden",
          position: "relative",
          ...bodyHtmlSx,
        }}
      >
        <HTML html={html} />
      </Box>
      {long && (
        <Link
          component="button"
          type="button"
          underline="always"
          onClick={() => setExpanded(!expanded)}
          sx={{
            mt: 1.25,
            cursor: "pointer",
            fontSize: 16,
            letterSpacing: "-0.33px",
            fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
            color: linkColor,
            border: "none",
            background: "none",
            p: 0,
          }}
        >
          {expanded ? <FormattedMessage id="see_less" /> : <FormattedMessage id="see_more" />}
        </Link>
      )}
    </Box>
  );
};

const ExpandableDescription = ({ html, linkColor }) => {
  const [expanded, setExpanded] = useState(false);
  if (!html) return null;
  const plain = String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const long = plain.length > 220;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          maxHeight: expanded ? "none" : 120,
          overflow: expanded ? "visible" : "hidden",
          "& p": {
            margin: "0 0 0.65em 0",
            fontSize: 16,
            lineHeight: 1.55,
            color: "#1B1B1B",
            fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
          },
        }}
      >
        <HTML html={html} />
      </Box>
      {long && (
        <Link
          component="button"
          type="button"
          underline="always"
          onClick={() => setExpanded(!expanded)}
          sx={{
            mt: 1,
            cursor: "pointer",
            fontSize: 16,
            letterSpacing: "-0.33px",
            fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
            color: linkColor,
            border: "none",
            background: "none",
            p: 0,
          }}
        >
          {expanded ? <FormattedMessage id="see_less" /> : <FormattedMessage id="see_more" />}
        </Link>
      )}
    </Box>
  );
};

const advantagePillSx = (index) => {
  const i = index % 4;
  if (i === 0) return { bgcolor: "#023331", color: "#fff", border: "none" };
  if (i === 1) return { bgcolor: "#166D4C", color: "#fff", border: "none" };
  if (i === 2) return { bgcolor: "transparent", color: "#023331", border: "1px solid #166D4C" };
  return { bgcolor: "transparent", color: "#023331", border: "1px solid #166D4C" };
};

const DashboardJobOfferDetailPanel = ({
  jobOffer,
  skills = [],
  onPublish,
  onCloseJob,
  onDuplicate,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const linkColor = theme.palette.primary.main;
  const cardSx = detailCardSx(theme);

  const publicJobUrl = `${window.location.origin}/${i18nLocale}/jobs/${jobOffer?.encoded_job_title}/${jobOffer?.id}`;

  const hardSkills = useMemo(
    () => (skills || []).filter((s) => s.category === "hard"),
    [skills]
  );
  const softSkills = useMemo(
    () => (skills || []).filter((s) => s.category === "soft"),
    [skills]
  );
  const langSkills = useMemo(
    () => (skills || []).filter((s) => s.category === "lang"),
    [skills]
  );

  const daysLeft =
    jobOffer?.end_of_mandate && jobOffer.offer_status === "online"
      ? Math.max(
        0,
        Math.ceil((new Date(jobOffer.end_of_mandate) - Date.now()) / 86400000)
      )
      : null;

  const publishedRef = jobOffer?.published_at || jobOffer?.created_at;
  const relativePosted =
    publishedRef && moment(publishedRef).isValid() ? moment(publishedRef).fromNow() : null;

  const locationsLine =
    jobOffer?.locations?.map((loc) => formatJobOfferLocation(loc)).filter(Boolean).join(" — ") ||
    "";

  const advantageItems = [];
  ["advantages", "schedules", "environments", "primes"].forEach((key) => {
    const arr = jobOffer?.offer_advantages?.[key];
    if (!Array.isArray(arr)) return;
    arr.forEach((value) => {
      let msgId = `job_offer_edit.${key === "advantages" ? "advantages" : key}.${value}`;
      if (key === "advantages") msgId = `job_offer_edit.advantages.${value}`;
      if (key === "schedules") msgId = `job_offer_edit.schedules.${value}`;
      if (key === "environments") msgId = `job_offer_edit.environment.${value}`;
      if (key === "primes") msgId = `job_offer_edit.primes.${value}`;
      advantageItems.push({ key: `${key}-${value}`, msgId });
    });
  });

  const tagChips = [];
  (jobOffer?.company_industries || []).forEach((ind) => {
    tagChips.push({
      key: `ind-${ind}`,
      label: intl.formatMessage({ id: `company.company_industry.${ind}`, defaultMessage: ind }),
      variant: "filled",
    });
  });
  (jobOffer?.job_statuses || []).forEach((st) => {
    tagChips.push({
      key: `js-${st}`,
      label: intl.formatMessage({ id: `candidate.status_map.${st}`, defaultMessage: st }),
      variant: "green",
    });
  });
  (jobOffer?.categories || []).forEach((cat) => {
    tagChips.push({
      key: `cat-${cat}`,
      label: intl.formatMessage({ id: `categories.${cat}`, defaultMessage: cat }),
      variant: "outline",
    });
  });
  if (jobOffer?.offer_language) {
    tagChips.push({
      key: `lang-${jobOffer.offer_language}`,
      label: intl.formatMessage({
        id: `languages.${jobOffer.offer_language}`,
        defaultMessage: jobOffer.offer_language,
      }),
      variant: "outline",
    });
  }

  const companyName = jobOffer?.company?.name || "—";

  const pillButtonSx = {
    borderRadius: "999px",
    textTransform: "none",
    fontFamily: "Segoe UI Semibold, Work Sans, sans-serif",
    minHeight: 44,
    px: 2.5,
  };

  return (
    <Stack spacing={2} component="section">
      <Box sx={cardSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent={daysLeft != null ? "space-between" : "flex-end"}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          {daysLeft != null && (
            <Chip
              label={intl.formatMessage({ id: "job_offer.n_days_left" }, { days: daysLeft })}
              size="small"
              sx={{
                alignSelf: "flex-start",
                bgcolor: "#CEE1DA",
                color: theme.palette.primary.main,
                fontWeight: 500,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
              }}
            />
          )}
          {jobOffer?.offer_status === "online" && (
            <JobOfferShareButtons
              publicJobUrl={publicJobUrl}
              jobTitle={jobOffer?.job_title}
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              sx={{ flex: 1 }}
            />
          )}
        </Stack>

        <Stack spacing={0} sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: 18, md: 20 },
              fontWeight: 600,
              color: "#222222",
              fontFamily: "Segoe UI Semibold, Work Sans, sans-serif",
              lineHeight: 1.33,
            }}
          >
            {companyName}
          </Typography>
          {locationsLine ? (
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: "#1B1B1B",
                mt: 0.75,
                fontFamily: "Segoe UI Semibold, Work Sans, sans-serif",
              }}
            >
              {locationsLine}
            </Typography>
          ) : null}
          {jobOffer?.job_title ? (
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 400,
                color: "#1B1B1B",
                mt: 1.25,
                lineHeight: 1.24,
                letterSpacing: 0.25,
                fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
              }}
            >
              {jobOffer.job_title}
            </Typography>
          ) : null}
        </Stack>

        {tagChips.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {tagChips.map((t) => {
              let sx = {
                height: 28,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
              };
              if (t.variant === "filled") {
                sx = { ...sx, bgcolor: "#023331", color: "#fff" };
              } else if (t.variant === "green") {
                sx = { ...sx, bgcolor: "#166D4C", color: "#fff" };
              } else {
                sx = {
                  ...sx,
                  bgcolor: "transparent",
                  color: "#023331",
                  border: "1px solid #166D4C",
                };
              }
              return <Chip key={t.key} label={t.label} sx={sx} />;
            })}
          </Stack>
        )}

        {jobOffer?.description ? (
          <ExpandableDescription html={jobOffer.description} linkColor={linkColor} />
        ) : null}

        {relativePosted && (
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 2, color: "#8E8E8E" }}>
            <FilterListOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: 15, fontFamily: "Segoe UI Regular, Work Sans, sans-serif" }}>
              {relativePosted}
            </Typography>
          </Stack>
        )}

        {jobOffer?.monthly_offer ? (
          <Typography
            sx={{
              fontSize: 16,
              color: "#1B1B1B",
              fontFamily: "Segoe UI Regular, Work Sans, sans-serif",
            }}
          >
            <FormattedMessage id="job_offer.monthly_offer" />: {annualyRemuneration(jobOffer.monthly_offer)}
          </Typography>
        ) : null}
      </Box>

      {jobOffer?.responsibilities ? (
        <Box sx={cardSx}>
          <ExpandableHtmlSection
            titleId="job_offer_creation.responsibilities_label"
            html={jobOffer.responsibilities}
            linkColor={linkColor}
          />
        </Box>
      ) : null}

      {jobOffer?.profile ? (
        <Box sx={cardSx}>
          <ExpandableHtmlSection titleId="job_offer.profile" html={jobOffer.profile} linkColor={linkColor} />
        </Box>
      ) : null}

      {jobOffer?.offer ? (
        <Box sx={cardSx}>
          <ExpandableHtmlSection
            titleId="job_offer.offer"
            html={jobOffer.offer}
            defaultExpanded
            linkColor={linkColor}
          />
        </Box>
      ) : null}

      {advantageItems.length > 0 && (
        <Box sx={cardSx}>
          <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>
            <FormattedMessage id="job_offer_edit.advantages_title" />
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.25}>
            {advantageItems.map((item, idx) => (
              <Chip
                key={item.key}
                label={<FormattedMessage id={item.msgId} />}
                sx={{
                  height: 28,
                  fontSize: 14,
                  borderRadius: "20px",
                  ...advantagePillSx(idx),
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {(hardSkills.length > 0 || softSkills.length > 0 || langSkills.length > 0) && (
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "10px",
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {hardSkills.length > 0 && (
            <>
              <Typography
                sx={{
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontSize: "16px",
                  color: "#1b1b1b",
                  letterSpacing: "0.15px",
                }}
              >
                <FormattedMessage id="candidate.profile.skills.hard" defaultMessage="Hard skills" />
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {hardSkills.map((skill, index) => (
                  <SkillView key={`hard-${index}`} value={skill} category="hard" level={false} locale={i18nLocale} />
                ))}
              </Box>
            </>
          )}

          {softSkills.length > 0 && (
            <>
              <Typography
                sx={{
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontSize: "16px",
                  color: "#1b1b1b",
                  letterSpacing: "0.15px",
                }}
              >
                <FormattedMessage id="candidate.profile.skills.soft" defaultMessage="Soft skills" />
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {softSkills.map((skill, index) => (
                  <SkillView key={`soft-${index}`} value={skill} category="soft" level={false} locale={i18nLocale} />
                ))}
              </Box>
            </>
          )}

          {langSkills.length > 0 && (
            <>
              <Typography
                sx={{
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontSize: "16px",
                  color: "#1b1b1b",
                  letterSpacing: "0.15px",
                }}
              >
                <FormattedMessage id="candidate.profile.skills.lang" defaultMessage="Language skills" />
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {langSkills.map((skill, index) => (
                  <SkillView key={`lang-${index}`} value={skill} category="lang" level={false} locale={i18nLocale} />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      <Box sx={cardSx}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "stretch" }}>
          {jobOffer.offer_status === "draft" && (
            <>
              <Button
                variant="contained"
                color="primary"
                sx={pillButtonSx}
                onClick={() => onPublish?.(jobOffer)}
              >
                <FormattedMessage id="job_offer.publish" />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={pillButtonSx}
                href={`/${i18nLocale}/recruiter/job_offer_creation?jobOffer=${jobOffer.id}`}
              >
                <FormattedMessage id="job_offer.edit" />
              </Button>
            </>
          )}
          {jobOffer.offer_status === "online" && (
            <>
              <Button
                variant="contained"
                color="primary"
                sx={{ ...pillButtonSx, flex: { sm: 1 } }}
                href={`/${i18nLocale}/recruiter/job_offer_creation?jobOffer=${jobOffer.id}`}
              >
                <FormattedMessage id="job_offer.edit" />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={{ ...pillButtonSx, flex: { sm: 1 }, borderColor: theme.palette.background.neutral, color: "#222" }}
                onClick={() => onCloseJob?.()}
              >
                <FormattedMessage id="job_offer.soft_delete" />
              </Button>
            </>
          )}
          {jobOffer.offer_status === "closed" && (
            <Button variant="contained" color="primary" sx={pillButtonSx} onClick={() => onDuplicate?.()}>
              <FormattedMessage id="job_offer.repost" />
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default DashboardJobOfferDetailPanel;
